import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Archive, FileText, Send, Upload, X, Calendar as CalendarIcon } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { StatusBadge } from "@/components/brand/StatusBadge";

// Constants
const MAX_IMAGE_MB = 3;
const DEFAULT_SEATS = 12;

const EMPTY_TOUR = {
  name: "",
  name_en: "",
  name_es: "",
  region: "",
  country: "Brasil",
  hero_image: "",
  summary: "",
  summary_en: "",
  summary_es: "",
  description: "",
  description_en: "",
  description_es: "",
  highlights_text: "",
  farms_text: "",
  duration_days: 7,
  price_per_person_usd: 1000,
  currency: "USD",
  available_dates: [], // [{ date: "YYYY-MM-DD", seats: int }]
  includes_text: "",
  supported_airports_text: "",
  best_seasons_text: "",
  status: "draft",
};

const linesToArr = (txt) => (txt || "").split("\n").map((s) => s.trim()).filter(Boolean);
const arrToLines = (arr) => (arr || []).join("\n");
const farmsToText = (farms) =>
  (farms || [])
    .map((f) => [f.name, f.city, f.altitude, f.specialty].filter(Boolean).join(" | "))
    .join("\n");
const textToFarms = (txt) =>
  linesToArr(txt).map((line) => {
    const parts = line.split("|").map((p) => p.trim());
    return { name: parts[0] || "", city: parts[1] || "", altitude: parts[2] || "", specialty: parts[3] || "" };
  });

const tourToForm = (t) => ({
  name: t.name || "",
  name_en: t.name_en || "",
  name_es: t.name_es || "",
  region: t.region || "",
  country: t.country || "Brasil",
  hero_image: t.hero_image || "",
  summary: t.summary || "",
  summary_en: t.summary_en || "",
  summary_es: t.summary_es || "",
  description: t.description || "",
  description_en: t.description_en || "",
  description_es: t.description_es || "",
  highlights_text: arrToLines(t.highlights),
  farms_text: farmsToText(t.farms),
  duration_days: t.duration_days || 7,
  price_per_person_usd: t.price_per_person_usd || 1000,
  currency: t.currency || "USD",
  available_dates: t.available_dates || [],
  includes_text: arrToLines(t.includes),
  supported_airports_text: arrToLines(t.supported_airports),
  best_seasons_text: arrToLines(t.best_seasons),
  status: t.status || "draft",
});

const formToPayload = (f) => ({
  name: f.name.trim(),
  name_en: f.name_en?.trim() || null,
  name_es: f.name_es?.trim() || null,
  region: f.region.trim(),
  country: f.country.trim() || "Brasil",
  hero_image: f.hero_image || null,
  summary: f.summary.trim(),
  summary_en: f.summary_en?.trim() || null,
  summary_es: f.summary_es?.trim() || null,
  description: f.description?.trim() || null,
  description_en: f.description_en?.trim() || null,
  description_es: f.description_es?.trim() || null,
  highlights: linesToArr(f.highlights_text),
  farms: textToFarms(f.farms_text),
  duration_days: Math.max(1, Number(f.duration_days) || 7),
  price_per_person_usd: Math.max(1, Number(f.price_per_person_usd) || 1000),
  currency: f.currency || "USD",
  available_dates: (f.available_dates || []).map((d) => ({
    date: d.date,
    seats: Math.max(1, Number(d.seats) || DEFAULT_SEATS),
  })),
  includes: linesToArr(f.includes_text),
  supported_airports: linesToArr(f.supported_airports_text),
  best_seasons: linesToArr(f.best_seasons_text),
  status: f.status,
});

// ---------- Image upload helpers ----------
const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const ymd = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export function AdminToursSection() {
  const { t, tData, formatPrice, formatDateTime } = useLocale();
  const [tours, setTours] = useState([]);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_TOUR);
  const [activeSub, setActiveSub] = useState("tours");
  const [statusFilter, setStatusFilter] = useState("all");
  const [uploading, setUploading] = useState(false);

  const reload = async () => {
    try {
      const [tr, rq] = await Promise.all([
        api.get("/tours/admin/all"),
        api.get("/tour-requests"),
      ]);
      setTours(tr.data);
      setRequests(rq.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  useEffect(() => { reload(); }, []);

  const filteredRequests = useMemo(() => {
    if (statusFilter === "all") return requests;
    return requests.filter((r) => r.status === statusFilter);
  }, [requests, statusFilter]);

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_TOUR);
    setOpen(true);
  };

  const openEdit = (tour) => {
    setEditingId(tour.id);
    setForm(tourToForm(tour));
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name || !form.region || !form.summary) {
      toast.error(t("admin_tours.form_required"));
      return;
    }
    try {
      const payload = formToPayload(form);
      if (editingId) {
        await api.patch(`/tours/${editingId}`, payload);
        toast.success(t("admin_tours.toast_updated"));
      } else {
        await api.post("/tours", payload);
        toast.success(t("admin_tours.toast_created"));
      }
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setStatus = async (tour, newStatus) => {
    try {
      await api.patch(`/tours/${tour.id}`, { status: newStatus });
      toast.success(t("admin_tours.toast_status"));
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (tour) => {
    if (!window.confirm(t("admin_tours.confirm_delete"))) return;
    try {
      await api.delete(`/tours/${tour.id}`);
      toast.success(t("admin_tours.toast_removed"));
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setRequestStatus = async (req, status) => {
    try {
      await api.patch(`/tour-requests/${req.id}`, { status });
      toast.success(t("admin_tours.req_toast_updated"));
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  // -------- Image upload --------
  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset for re-selecting same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("admin_tours.err_not_image"));
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      toast.error(t("admin_tours.err_image_too_big").replace("{mb}", MAX_IMAGE_MB));
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await readFileAsDataURL(file);
      setForm((f) => ({ ...f, hero_image: dataUrl }));
    } catch {
      toast.error(t("admin_tours.err_image_read"));
    } finally {
      setUploading(false);
    }
  };

  // -------- Calendar multi-select --------
  const selectedDateObjs = useMemo(
    () => (form.available_dates || []).map((d) => new Date(d.date + "T12:00:00")),
    [form.available_dates],
  );

  const onCalendarChange = (days) => {
    // `days` is an array of Date objects (mode="multiple")
    const incoming = (days || []).map(ymd);
    setForm((f) => {
      const existing = new Map((f.available_dates || []).map((d) => [d.date, d.seats]));
      const next = incoming.map((date) => ({
        date,
        seats: existing.get(date) || DEFAULT_SEATS,
      }));
      // keep sorted by date for stable UI
      next.sort((a, b) => (a.date < b.date ? -1 : 1));
      return { ...f, available_dates: next };
    });
  };

  const removeDate = (date) => {
    setForm((f) => ({
      ...f,
      available_dates: (f.available_dates || []).filter((d) => d.date !== date),
    }));
  };

  const setSeats = (date, seats) => {
    setForm((f) => ({
      ...f,
      available_dates: (f.available_dates || []).map((d) =>
        d.date === date ? { ...d, seats: Math.max(1, Number(seats) || 1) } : d,
      ),
    }));
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeSub} onValueChange={setActiveSub}>
        <TabsList className="rounded-none border border-border bg-card h-auto p-0">
          <TabsTrigger
            value="tours"
            data-testid="admin-tours-subtab-packages"
            className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
          >
            {t("admin_tours.subtab_packages")} · {tours.length}
          </TabsTrigger>
          <TabsTrigger
            value="requests"
            data-testid="admin-tours-subtab-requests"
            className="rounded-none px-5 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
          >
            {t("admin_tours.subtab_requests")} · {requests.length}
          </TabsTrigger>
        </TabsList>

        {/* PACKAGES */}
        <TabsContent value="tours" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-muted-foreground">{t("admin_tours.packages_help")}</p>
            <Button
              onClick={openNew}
              data-testid="admin-tours-new-btn"
              className="rounded-none h-9 bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-wider"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" /> {t("admin_tours.new_btn")}
            </Button>
          </div>

          <div className="border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left p-3">{t("admin_tours.col_name")}</th>
                  <th className="text-left p-3">{t("common.region")}</th>
                  <th className="text-right p-3">{t("admin_tours.col_days")}</th>
                  <th className="text-right p-3">{t("admin_tours.col_base_price")}</th>
                  <th className="text-right p-3">{t("admin_tours.col_seats")}</th>
                  <th className="text-left p-3">{t("admin_tours.col_status")}</th>
                  <th className="text-right p-3">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {tours.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">{t("admin_tours.empty")}</td></tr>
                ) : tours.map((tour) => {
                  const dates = tour.available_dates || [];
                  const totalSeats = dates.reduce((s, d) => s + (Number(d.seats) || 0), 0);
                  return (
                    <tr key={tour.id} className="border-b border-border/60 hover:bg-secondary/20">
                      <td className="p-3">
                        <div className="flex items-center gap-3">
                          {tour.hero_image && (
                            <img src={tour.hero_image} alt="" className="h-10 w-10 object-cover border border-border" />
                          )}
                          <div>
                            <div className="font-medium truncate max-w-[260px]">{tour.name}</div>
                            <div className="text-xs text-muted-foreground">{tour.farms?.length || 0} {t("admin_tours.farms_count")}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">{tData("region", tour.region)}</td>
                      <td className="p-3 text-right font-mono-data">{tour.duration_days || (tour.duration_options || [7])[0]}d</td>
                      <td className="p-3 text-right font-mono-data text-accent">
                        {formatPrice(tour.price_per_person_usd || tour.price_per_person_7d_usd, "USD")}
                      </td>
                      <td className="p-3 text-right font-mono-data text-xs">
                        {dates.length > 0 ? `${dates.length}d · ${totalSeats}` : "—"}
                      </td>
                      <td className="p-3"><StatusBadge status={tour.status} /></td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          {tour.status === "draft" && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setStatus(tour, "published")}
                              data-testid={`admin-tour-${tour.id}-publish`}
                              className="h-7 rounded-none text-[10px] uppercase tracking-wider"
                            >
                              <Send className="h-3 w-3 mr-1" /> {t("admin_tours.publish")}
                            </Button>
                          )}
                          {tour.status === "published" && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => setStatus(tour, "draft")}
                              className="h-7 rounded-none text-[10px] uppercase tracking-wider"
                            >
                              <FileText className="h-3 w-3 mr-1" /> {t("admin_tours.draft")}
                            </Button>
                          )}
                          {tour.status !== "archived" && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => setStatus(tour, "archived")}
                              className="h-7 rounded-none text-[10px] uppercase tracking-wider"
                            >
                              <Archive className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => openEdit(tour)}
                            data-testid={`admin-tour-${tour.id}-edit`}
                            className="h-7 rounded-none"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => remove(tour)}
                            data-testid={`admin-tour-${tour.id}-delete`}
                            className="h-7 rounded-none text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* REQUESTS */}
        <TabsContent value="requests" className="mt-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <p className="text-xs text-muted-foreground">{t("admin_tours.requests_help")}</p>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="admin-tour-requests-status-filter" className="rounded-none w-[200px] h-9 font-mono-data text-xs uppercase">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all")}</SelectItem>
                <SelectItem value="pending">{t("admin_tours.req_pending")}</SelectItem>
                <SelectItem value="confirmed">{t("admin_tours.req_confirmed")}</SelectItem>
                <SelectItem value="cancelled">{t("admin_tours.req_cancelled")}</SelectItem>
                <SelectItem value="completed">{t("admin_tours.req_completed")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/30 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left p-3">{t("admin_tours.req_order")}</th>
                  <th className="text-left p-3">{t("admin_tours.req_buyer")}</th>
                  <th className="text-left p-3">{t("admin_tours.req_tour")}</th>
                  <th className="text-left p-3">{t("tours.start_date")}</th>
                  <th className="text-right p-3">{t("tours.pax_short")}</th>
                  <th className="text-right p-3">{t("admin_tours.req_total")}</th>
                  <th className="text-left p-3">{t("admin_tours.req_payment")}</th>
                  <th className="text-left p-3">{t("common.status")}</th>
                  <th className="text-left p-3">{t("admin_tours.req_when")}</th>
                  <th className="text-right p-3">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.length === 0 ? (
                  <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">{t("admin_tours.req_empty")}</td></tr>
                ) : filteredRequests.map((req) => (
                  <tr key={req.id} className="border-b border-border/60 hover:bg-secondary/20">
                    <td className="p-3 font-mono-data text-xs">
                      {req.order_number ? (
                        <span className="text-accent">{req.order_number}</span>
                      ) : <span className="text-muted-foreground/50">—</span>}
                    </td>
                    <td className="p-3">
                      <div className="font-medium">{req.user_company || req.user_email}</div>
                      <div className="text-xs text-muted-foreground">{req.user_email}</div>
                    </td>
                    <td className="p-3 truncate max-w-[200px]">{req.tour_name}</td>
                    <td className="p-3 font-mono-data text-xs">{req.selected_date || req.preferred_start_date || "—"}</td>
                    <td className="p-3 text-right font-mono-data">{req.passengers}</td>
                    <td className="p-3 text-right font-mono-data text-accent">{formatPrice(req.total_price_usd, "USD")}</td>
                    <td className="p-3">
                      {req.payment_status === "paid" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border border-emerald-500/40 bg-emerald-500/10 text-emerald-600">
                          ● {t("admin_tours.payment_paid")}
                        </span>
                      ) : (
                        <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground/60">
                          {t("admin_tours.payment_unpaid")}
                        </span>
                      )}
                      {req.card_brand && req.card_last4 && (
                        <div className="text-[10px] font-mono-data text-muted-foreground mt-1">
                          {req.card_brand} ****{req.card_last4}
                        </div>
                      )}
                    </td>
                    <td className="p-3"><StatusBadge status={req.status} /></td>
                    <td className="p-3 text-xs text-muted-foreground">{formatDateTime(req.created_at)}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {req.status === "pending" && (
                          <>
                            <Button size="sm" onClick={() => setRequestStatus(req, "confirmed")} data-testid={`admin-req-${req.id}-confirm`} className="h-7 rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] uppercase tracking-wider">
                              {t("admin_tours.confirm")}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setRequestStatus(req, "cancelled")} data-testid={`admin-req-${req.id}-cancel`} className="h-7 rounded-none text-[10px] uppercase tracking-wider">
                              {t("admin_tours.cancel")}
                            </Button>
                          </>
                        )}
                        {req.status === "confirmed" && (
                          <Button size="sm" variant="outline" onClick={() => setRequestStatus(req, "completed")} className="h-7 rounded-none text-[10px] uppercase tracking-wider">
                            {t("admin_tours.complete")}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl rounded-none max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingId ? t("admin_tours.dialog_edit") : t("admin_tours.dialog_new")}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {t("admin_tours.packages_help")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-2">

            {/* === HERO IMAGE UPLOAD === */}
            <section>
              <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-accent mb-3">
                {t("admin_tours.section_image")}
              </div>
              <div className="border border-dashed border-border bg-secondary/30 p-4">
                {form.hero_image ? (
                  <div className="flex items-start gap-4">
                    <img
                      src={form.hero_image}
                      alt="hero preview"
                      className="h-32 w-48 object-cover border border-border"
                      data-testid="admin-tour-hero-preview"
                    />
                    <div className="flex-1 space-y-2">
                      <p className="text-xs text-muted-foreground">{t("admin_tours.hero_replace_help")}</p>
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer inline-flex">
                          <input type="file" accept="image/*" onChange={onPickImage} className="hidden" data-testid="admin-tour-hero-input" />
                          <span className="inline-flex items-center gap-2 h-9 px-3 border border-border bg-background hover:bg-secondary font-mono-data text-xs uppercase tracking-wider">
                            <Upload className="h-3.5 w-3.5" /> {t("admin_tours.replace_image")}
                          </span>
                        </label>
                        <Button
                          variant="ghost"
                          onClick={() => setForm((f) => ({ ...f, hero_image: "" }))}
                          className="h-9 rounded-none text-destructive text-xs uppercase tracking-wider"
                        >
                          <X className="h-3.5 w-3.5 mr-1" /> {t("admin_tours.remove_image")}
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <label className="block cursor-pointer text-center py-8">
                    <input type="file" accept="image/*" onChange={onPickImage} className="hidden" data-testid="admin-tour-hero-input" />
                    <Upload className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                    <div className="font-mono-data text-xs uppercase tracking-wider text-foreground">
                      {uploading ? t("admin_tours.image_uploading") : t("admin_tours.image_upload_cta")}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {t("admin_tours.image_hint").replace("{mb}", MAX_IMAGE_MB)}
                    </div>
                  </label>
                )}
              </div>
            </section>

            {/* === BASIC INFO === */}
            <section>
              <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-accent mb-3">
                {t("admin_tours.section_basic")}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label={`${t("admin_tours.field_name")} (PT)`} required>
                  <Input data-testid="admin-tour-name-pt" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
                </Field>
                <Field label={`${t("admin_tours.field_name")} (EN)`}>
                  <Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className="rounded-none" />
                </Field>
                <Field label={`${t("admin_tours.field_name")} (ES)`}>
                  <Input value={form.name_es} onChange={(e) => setForm({ ...form, name_es: e.target.value })} className="rounded-none" />
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                <Field label={t("common.region")} required>
                  <Input data-testid="admin-tour-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="rounded-none" />
                </Field>
                <Field label={t("admin_tours.field_country")}>
                  <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-none" />
                </Field>
                <Field label={t("admin_tours.field_duration_days")} hint={t("admin_tours.duration_hint")}>
                  <Input
                    type="number" min="1" max="60" step="1"
                    data-testid="admin-tour-duration-days"
                    value={form.duration_days}
                    onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                    className="rounded-none"
                  />
                </Field>
                <Field label={t("common.status")}>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="admin-tour-status" className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">{t("admin_tours.status_draft")}</SelectItem>
                      <SelectItem value="published">{t("admin_tours.status_published")}</SelectItem>
                      <SelectItem value="archived">{t("admin_tours.status_archived")}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </section>

            {/* === PRICE & AVAILABILITY === */}
            <section>
              <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-accent mb-3">
                {t("admin_tours.section_pricing")}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <Field label={t("admin_tours.field_price_per_person")} hint={t("admin_tours.price_hint")} required>
                  <Input
                    type="number" min="1" step="50"
                    data-testid="admin-tour-price"
                    value={form.price_per_person_usd}
                    onChange={(e) => setForm({ ...form, price_per_person_usd: e.target.value })}
                    className="rounded-none"
                  />
                </Field>
              </div>

              <div className="border border-border bg-secondary/30 p-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <Label className="text-xs font-mono-data uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5" /> {t("admin_tours.field_dates_seats")}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" data-testid="admin-tour-dates-picker" className="h-8 rounded-none text-xs uppercase tracking-wider">
                        <Plus className="h-3 w-3 mr-1" /> {t("admin_tours.pick_dates")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-none" align="end">
                      <Calendar
                        mode="multiple"
                        selected={selectedDateObjs}
                        onSelect={onCalendarChange}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {(form.available_dates || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">{t("admin_tours.dates_empty")}</p>
                ) : (
                  <div className="space-y-2">
                    {(form.available_dates || []).map((d) => (
                      <div key={d.date} className="flex items-center gap-2 bg-background border border-border px-3 py-2" data-testid={`admin-tour-date-${d.date}`}>
                        <CalendarIcon className="h-3.5 w-3.5 text-accent" />
                        <span className="font-mono-data text-sm flex-1">{d.date}</span>
                        <Label className="text-[10px] font-mono-data uppercase tracking-wider text-muted-foreground">
                          {t("admin_tours.seats_label")}
                        </Label>
                        <Input
                          type="number" min="1" step="1"
                          value={d.seats}
                          onChange={(e) => setSeats(d.date, e.target.value)}
                          className="w-20 h-8 rounded-none text-right font-mono-data"
                          data-testid={`admin-tour-date-${d.date}-seats`}
                        />
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => removeDate(d.date)}
                          className="h-7 w-7 rounded-none text-destructive"
                          aria-label="Remove date"
                          data-testid={`admin-tour-date-${d.date}-remove`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* === DESCRIPTION === */}
            <section>
              <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-accent mb-3">
                {t("admin_tours.section_content")}
              </div>
              <Field label={`${t("admin_tours.field_summary")} (PT)`} hint={t("admin_tours.summary_hint")} required>
                <Textarea data-testid="admin-tour-summary-pt" rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="rounded-none" />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <Field label={`${t("admin_tours.field_summary")} (EN)`}>
                  <Textarea rows={2} value={form.summary_en} onChange={(e) => setForm({ ...form, summary_en: e.target.value })} className="rounded-none" />
                </Field>
                <Field label={`${t("admin_tours.field_summary")} (ES)`}>
                  <Textarea rows={2} value={form.summary_es} onChange={(e) => setForm({ ...form, summary_es: e.target.value })} className="rounded-none" />
                </Field>
              </div>

              <div className="mt-4">
                <Field label={`${t("admin_tours.field_description")} (PT)`} hint={t("admin_tours.description_hint")}>
                  <Textarea data-testid="admin-tour-description-pt" rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" />
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <Field label={`${t("admin_tours.field_description")} (EN)`}>
                  <Textarea rows={4} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="rounded-none" />
                </Field>
                <Field label={`${t("admin_tours.field_description")} (ES)`}>
                  <Textarea rows={4} value={form.description_es} onChange={(e) => setForm({ ...form, description_es: e.target.value })} className="rounded-none" />
                </Field>
              </div>
            </section>

            {/* === LOGISTICS / EXTRAS === */}
            <section>
              <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-accent mb-3">
                {t("admin_tours.section_extras")}
              </div>
              <Field label={t("admin_tours.field_highlights")} hint={t("admin_tours.hint_one_per_line")}>
                <Textarea rows={3} value={form.highlights_text} onChange={(e) => setForm({ ...form, highlights_text: e.target.value })} className="rounded-none" />
              </Field>
              <div className="mt-3">
                <Field label={t("admin_tours.field_farms")} hint={t("admin_tours.hint_farms")}>
                  <Textarea
                    rows={4}
                    value={form.farms_text}
                    placeholder="Fazenda Boa Vista | Carmo de Minas | 1.280m | Bourbon Amarelo, Geisha"
                    onChange={(e) => setForm({ ...form, farms_text: e.target.value })}
                    className="rounded-none font-mono-data text-xs"
                  />
                </Field>
              </div>
              <div className="mt-3">
                <Field label={t("admin_tours.field_includes")} hint={t("admin_tours.hint_one_per_line")}>
                  <Textarea rows={3} value={form.includes_text} onChange={(e) => setForm({ ...form, includes_text: e.target.value })} className="rounded-none" />
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <Field label={t("admin_tours.field_airports")} hint={t("admin_tours.hint_one_per_line")}>
                  <Textarea
                    rows={3}
                    value={form.supported_airports_text}
                    placeholder="GRU - São Paulo / Guarulhos"
                    onChange={(e) => setForm({ ...form, supported_airports_text: e.target.value })}
                    className="rounded-none font-mono-data text-xs"
                  />
                </Field>
                <Field label={t("admin_tours.field_seasons")} hint={t("admin_tours.hint_one_per_line")}>
                  <Textarea rows={3} value={form.best_seasons_text} onChange={(e) => setForm({ ...form, best_seasons_text: e.target.value })} className="rounded-none" />
                </Field>
              </div>
            </section>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-none">{t("common.cancel")}</Button>
            <Button
              onClick={submit}
              data-testid="admin-tour-submit"
              className="rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-wider"
            >
              {editingId ? t("admin_tours.save_btn") : t("admin_tours.create_btn")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-mono-data uppercase tracking-wider text-muted-foreground">
        {label} {required && <span className="text-accent">*</span>}
      </Label>
      {children}
      {hint && <p className="text-[10px] text-muted-foreground/70">{hint}</p>}
    </div>
  );
}
